// Modal de confirmação no padrão Aeros Dark (mockups "Modal Bloquear A" e
// "Modal Excluir A"): ícone em bloco colorido, título, texto, confirmação
// opcional por palavra digitada e botões Cancelar / ação.
import React, { useState, useEffect } from "react";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import Button from "@material-ui/core/Button";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    paper: { width: 420, maxWidth: "calc(100vw - 32px)", margin: 16 },
    body: { padding: "22px 24px 0", display: "flex", gap: 14 },
    iconBox: {
      width: 40,
      height: 40,
      borderRadius: 11,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      "& .MuiSvgIcon-root": { fontSize: 21 },
    },
    iconDanger: { backgroundColor: alpha(AEROS.error, 0.12), color: AEROS.error },
    iconInfo: { backgroundColor: alpha(AEROS.cyan, 0.12), color: AEROS.cyan },
    iconWarning: { backgroundColor: alpha(AEROS.warning, 0.12), color: AEROS.warning },
    title: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 16,
      fontWeight: 700,
      color: theme.palette.text.primary,
      lineHeight: 1.3,
    },
    text: {
      fontSize: 13,
      color: theme.palette.text.secondary,
      lineHeight: 1.6,
      marginTop: 6,
    },
    typed: {
      marginTop: 12,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      borderRadius: 9,
      padding: "6px 11px",
      fontSize: 12.5,
      width: "100%",
      color: theme.palette.text.primary,
      "& input::placeholder": { color: theme.palette.aeros.textTertiary, opacity: 1 },
      "&.Mui-focused": {
        borderColor: AEROS.cyan,
        boxShadow: "0 0 0 3px rgba(34,211,238,0.15)",
      },
    },
    typedHint: {
      fontSize: 11.5,
      color: theme.palette.aeros.textTertiary,
      marginTop: 6,
      "& b": { color: theme.palette.text.primary },
    },
    actions: {
      padding: "16px 24px 20px",
      display: "flex",
      justifyContent: "flex-end",
      gap: 10,
    },
    cancel: {
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.16)"}`,
      color: theme.palette.text.primary,
      borderRadius: 10,
      padding: "7px 16px",
      fontWeight: 600,
      fontSize: 13,
    },
    confirm: {
      borderRadius: 10,
      padding: "7px 16px",
      fontWeight: 600,
      fontSize: 13,
      position: "relative",
    },
    confirmDanger: {
      color: stateText(theme, AEROS.error, "error"),
      backgroundColor: alpha(AEROS.error, 0.12),
      border: `1px solid ${alpha(AEROS.error, 0.4)}`,
      "&:hover": { backgroundColor: AEROS.error, color: "#07080D" },
      "&.Mui-disabled": {
        backgroundColor: isLight ? "rgba(15,20,40,0.06)" : "rgba(255,255,255,0.06)",
        borderColor: "transparent",
        color: theme.palette.aeros.textTertiary,
      },
    },
  };
});

/**
 * Normaliza os dois lados da confirmação digitada do mesmo jeito.
 *
 * `typedWord` pode ser dado do banco — em `/companies` é o nome da empresa, e
 * nada apara o nome na criação nem na edição. Nome composto colado de outro
 * documento chega com espaço sobrando na ponta, espaço duplo no meio ou espaço
 * não-quebrável (` `); como só o que o usuário digita era aparado, esses
 * nomes nunca batiam e o botão de excluir ficava travado para sempre — sem o
 * usuário ver a diferença, porque no placeholder ela é invisível.
 */
export const normalizaConfirmacao = (valor) =>
  String(valor ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

const AerosConfirmModal = ({
  open,
  onClose,
  onConfirm,
  icon,
  tone = "danger", // danger | info | warning
  title,
  description,
  confirmLabel,
  cancelLabel,
  typedWord, // ex.: "EXCLUIR" — habilita o botão só quando digitado
  typedHint,
  loading = false,
  primary = false, // botão de ação em gradiente em vez de destrutivo
}) => {
  const classes = useStyles();
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);

  const typedOk = !typedWord || normalizaConfirmacao(typed) === normalizaConfirmacao(typedWord);
  const iconClass =
    tone === "info" ? classes.iconInfo : tone === "warning" ? classes.iconWarning : classes.iconDanger;

  return (
    <Dialog open={Boolean(open)} onClose={loading ? undefined : onClose} classes={{ paper: classes.paper }}>
      <div className={classes.body}>
        {icon && <div className={`${classes.iconBox} ${iconClass}`}>{icon}</div>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className={classes.title}>{title}</div>
          {description && <div className={classes.text}>{description}</div>}
          {typedWord && (
            <>
              <InputBase
                className={classes.typed}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={typedWord}
                autoFocus
                inputProps={{ "aria-label": typedWord }}
              />
              <div className={classes.typedHint}>
                {typedHint || i18n.t("aeros.confirm.typedHint", { word: typedWord })}
              </div>
            </>
          )}
        </div>
      </div>
      <div className={classes.actions}>
        <Button variant="outlined" className={classes.cancel} onClick={onClose} disabled={loading}>
          {cancelLabel || i18n.t("aeros.confirm.cancel")}
        </Button>
        <Button
          variant={primary ? "contained" : "text"}
          color={primary ? "primary" : "default"}
          className={`${classes.confirm} ${primary ? "" : classes.confirmDanger}`}
          onClick={onConfirm}
          disabled={!typedOk || loading}
        >
          {confirmLabel || i18n.t("aeros.confirm.confirm")}
          {loading && (
            <CircularProgress
              size={18}
              style={{ position: "absolute", left: "50%", top: "50%", marginLeft: -9, marginTop: -9 }}
            />
          )}
        </Button>
      </div>
    </Dialog>
  );
};

export default AerosConfirmModal;
